import React, { useState } from 'react';
import { Box, Typography, IconButton, Button, CircularProgress, Alert, Stack } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { LicitacionesService } from '../../services/licitaciones.service';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface ArchivoApi {
  id?: number;
  nombre: string;
  url_externa: string;
  path_local: string;
}

interface ArchivosProps {
  archivos?: ArchivoApi[];
  entityType?: 'licitaciones' | 'compras-agiles';
  //nuevo
  codigoExterno?: string;
  tematicaId?: number;
  urlFichaOficial?: string;
  onRefresh?: () => void;
}

const getFileType = (fileName: string): 'pdf' | 'doc' | 'image' | 'other' => {
  const extension = fileName.toLowerCase().split('.').pop();
  
  switch (extension) {
    case 'pdf':
      return 'pdf';
    case 'doc':
    case 'docx':
    case 'txt':
    case 'rtf':
      return 'doc';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'svg':
      return 'image';
    default:
      return 'other';
  }
};

const getIcon = (tipo: string) => {
  switch (tipo) {
    case 'pdf':
      return <PictureAsPdfIcon sx={{ color: '#d32f2f', fontSize: { xs: '20px', sm: '24px' } }} />;
    case 'doc':
      return <DescriptionIcon sx={{ color: '#1976d2', fontSize: { xs: '20px', sm: '24px' } }} />;
    case 'image':
      return <ImageIcon sx={{ color: '#43a047', fontSize: { xs: '20px', sm: '24px' } }} />;
    default:
      return <DescriptionIcon sx={{ color: '#757575', fontSize: { xs: '20px', sm: '24px' } }} />;
  }
};

const Archivos: React.FC<ArchivosProps> = ({ archivos = [], entityType = 'licitaciones', codigoExterno, urlFichaOficial, 
  onRefresh, tematicaId, }) => {

  console.log("📌 [Archivos Component] Código Externo:", codigoExterno, "| Temática ID:", tematicaId);

  const [subiendo, setSubiendo] = useState(false);
  const [analizando, setAnalizando] = useState(false);
  const [notificacion, setNotificacion] = useState<{ tipo: 'success' | 'error'; mensaje: string } | null>(null);
  const handleDownload = async (archivo: ArchivoApi) => {
    //nuevo
   
    if (entityType === 'licitaciones' && archivo.id) {
      try {
        const signedUrl = await LicitacionesService.obtenerLinkDescarga(archivo.id);
        window.open(signedUrl, '_blank');
      } catch {
        // Si falla el signed link, fallback a la URL externa del archivo
        if (archivo.url_externa) {
          window.open(archivo.url_externa, '_blank');
        }
      }
    } else {
      window.open(archivo.url_externa, '_blank');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !codigoExterno) return;

    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('archivos', file));

    try {
      setSubiendo(true);
      setNotificacion(null);
      await LicitacionesService.subirAdjuntosManual(codigoExterno, formData);

      setNotificacion({ tipo: 'success', mensaje: 'Archivos subidos correctamente' });
      if (onRefresh) onRefresh();
    } catch (err: any) {
      setNotificacion({ tipo: 'error', mensaje: err.message || 'Error al subir los archivos' });
    } finally {
      setSubiendo(false);
      event.target.value = '';
    }
  };

  const handleAnalizar = async () => {
    if (!codigoExterno) return;
    console.log("🔍 [Archivos.tsx] Clic en analizar. Código:", codigoExterno, "Temática ID:", tematicaId);
    try {
      setAnalizando(true);
      setNotificacion(null);
      await LicitacionesService.analizarDocumentos(codigoExterno, tematicaId);

      setNotificacion({ tipo: 'success', mensaje: 'Análisis completado exitosamente' });
      if (onRefresh) onRefresh();
    } catch (err: any) {
      setNotificacion({ tipo: 'error', mensaje: err.message || 'Error al analizar los documentos' });
    } finally {
      setAnalizando(false);
    }
  };
  


  return (
    <Box sx={{ width: '100%', mt: { xs: 1, sm: 2 } }}>
      {/* 1. Botones de Acción (Subir y Analizar) */}
      {entityType === 'licitaciones' && codigoExterno && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#fcfcfc', border: '1px solid #E0E0E0', borderRadius: '8px' }}>
          <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="center">



            {urlFichaOficial && (
              <Button
                variant="text"
                size="small"
                href={urlFichaOficial} // <-- Usa la URL larga con el 'enc' del primer archivo
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewIcon />}
                sx={{ color: '#1976d2', textTransform: 'none', fontWeight: 500 }}
              >
                Descargar Documentos Oficiales en Mercado Público
              </Button>
            )}
            
            
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                component="label"
                size="small"
                startIcon={subiendo ? <CircularProgress size={16} /> : <UploadFileIcon />}
                disabled={subiendo || analizando}
              >
                {subiendo ? 'Subiendo...' : 'Subir Adjuntos'}
                <input type="file" hidden multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.zip" onChange={handleFileUpload} />
              </Button>

              <Button
                variant="contained"
                size="small"
                startIcon={analizando ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                onClick={handleAnalizar}
                disabled={analizando || subiendo}
              >
                {analizando ? 'Analizando...' : 'Analizar con IA'}
              </Button>
            </Stack>
          </Stack>

          {notificacion && (
            <Alert severity={notificacion.tipo} sx={{ mt: 1.5 }} onClose={() => setNotificacion(null)}>
              {notificacion.mensaje}
            </Alert>
          )}
        </Box>
      )}

      {/* 2. Si no hay archivos, muestra el estado vacío */}
      {!archivos || archivos.length === 0 ? (
        <Box 
          sx={{ 
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            py: 4 
          }}
        >
          <FolderOpenIcon sx={{ fontSize: 48, color: '#9E9E9E', mb: 2 }} />
          <Typography sx={{ color: '#9E9E9E', fontSize: { xs: '14px', sm: '16px' }, textAlign: 'center' }}>
            No hay archivos asociados a esta {entityType === 'compras-agiles' ? 'compra ágil' : 'licitación'}
          </Typography>
        </Box>
      ) : (
        /* 3. Si hay archivos, muestra el listado en grid */
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
            gap: { xs: 1, sm: 2 } 
          }}
        >
          {archivos.map((archivo, index) => {
            const tipoArchivo = getFileType(archivo.nombre);
            
            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: { xs: 1.5, sm: 2 },
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  minWidth: 0,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    cursor: 'pointer'
                  }
                }}
              >
                <Box sx={{ mr: { xs: 1, sm: 2 }, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  {getIcon(tipoArchivo)}
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                    color: '#585858',
                    fontSize: { xs: '13px', sm: '14px', md: '16px' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0
                  }}
                >
                  {archivo.nombre}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleDownload(archivo)}
                  sx={{
                    ml: { xs: 0.5, sm: 1 },
                    p: { xs: 0.5, sm: 1 },
                    color: '#1976d2',
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  <FileDownloadIcon />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default Archivos;